import { EditableEntityState } from "@stenajs-webui/redux";
import _, { isEqual } from "lodash";
import { ExpectedVolumesByRoutePair } from "../redux/ExpectedVolumeRedux";
import { ExpectedVolumeTableRow } from "../types";

export interface ExpectedVolumesChanges {
  routePairId: string;
  modifiedVolumes: Array<ModifiedVolumeForChange>;
}

export interface ModifiedVolumeForChange {
  customerNo: string;
  accompaniedExpectedVolOffPeak: number | null;
  accompaniedExpectedVolPeak: number | null;
  accompaniedExpectedVolShoulder: number | null;
  unaccompaniedExpectedVolOffPeak: number | null;
  unaccompaniedExpectedVolPeak: number | null;
  unaccompaniedExpectedVolShoulder: number | null;
  volumeAgreed: boolean;
}

export const getExpectedVolumeChanges = (
  expectedVolumesByRoutePair: ExpectedVolumesByRoutePair
): Array<ExpectedVolumesChanges> => {
  const routePairIds = Object.keys(expectedVolumesByRoutePair);
  return routePairIds
    .map((routePairId): ExpectedVolumesChanges => {
      const { volumes } = expectedVolumesByRoutePair[routePairId];
      return {
        routePairId,
        modifiedVolumes: volumes
          .filter(volumeIsModified)
          .map((a): ModifiedVolumeForChange => {
            if (a.editable.volumeAgreed !== a.persisted.volumeAgreed) {
              return {
                customerNo: a.editable.customerNo,
                volumeAgreed: a.editable.volumeAgreed,
                accompaniedExpectedVolOffPeak:
                  a.editable.accompaniedExpectedVolOffPeak ?? 0,
                accompaniedExpectedVolPeak:
                  a.editable.accompaniedExpectedVolPeak ?? 0,
                accompaniedExpectedVolShoulder:
                  a.editable.accompaniedExpectedVolShoulder ?? 0,
                unaccompaniedExpectedVolOffPeak:
                  a.editable.unaccompaniedExpectedVolOffPeak ?? 0,
                unaccompaniedExpectedVolPeak:
                  a.editable.unaccompaniedExpectedVolPeak ?? 0,
                unaccompaniedExpectedVolShoulder:
                  a.editable.unaccompaniedExpectedVolShoulder ?? 0,
              };
            }
            const updatedValues = {
              customerNo: a.editable.customerNo,
              volumeAgreed: a.editable.volumeAgreed,
            };

            if (
              valueIsUpdatedChecker(
                a.editable.accompaniedExpectedVolOffPeak,
                a.persisted.accompaniedExpectedVolOffPeak
              )
            ) {
              _.set(
                updatedValues,
                "accompaniedExpectedVolOffPeak",
                a.editable.accompaniedExpectedVolOffPeak ?? 0
              );
            }
            if (
              valueIsUpdatedChecker(
                a.editable.accompaniedExpectedVolPeak,
                a.persisted.accompaniedExpectedVolPeak
              )
            ) {
              _.set(
                updatedValues,
                "accompaniedExpectedVolPeak",
                a.editable.accompaniedExpectedVolPeak ?? 0
              );
            }
            if (
              valueIsUpdatedChecker(
                a.editable.accompaniedExpectedVolShoulder,
                a.persisted.accompaniedExpectedVolShoulder
              )
            ) {
              _.set(
                updatedValues,
                "accompaniedExpectedVolShoulder",
                a.editable.accompaniedExpectedVolShoulder ?? 0
              );
            }
            if (
              valueIsUpdatedChecker(
                a.editable.unaccompaniedExpectedVolOffPeak,
                a.persisted.unaccompaniedExpectedVolOffPeak
              )
            ) {
              _.set(
                updatedValues,
                "unaccompaniedExpectedVolOffPeak",
                a.editable.unaccompaniedExpectedVolOffPeak ?? 0
              );
            }
            if (
              valueIsUpdatedChecker(
                a.editable.unaccompaniedExpectedVolPeak,
                a.persisted.unaccompaniedExpectedVolPeak
              )
            ) {
              _.set(
                updatedValues,
                "unaccompaniedExpectedVolPeak",
                a.editable.unaccompaniedExpectedVolPeak ?? 0
              );
            }
            if (
              valueIsUpdatedChecker(
                a.editable.unaccompaniedExpectedVolShoulder,
                a.persisted.unaccompaniedExpectedVolShoulder
              )
            ) {
              _.set(
                updatedValues,
                "unaccompaniedExpectedVolShoulder",
                a.editable.unaccompaniedExpectedVolShoulder ?? 0
              );
            }
            return updatedValues as ModifiedVolumeForChange;
          }),
      };
    })
    .filter((p) => p.modifiedVolumes.length > 0);
};

const volumeIsModified = (
  a: EditableEntityState<ExpectedVolumeTableRow>
): boolean => {
  if (a.persisted === a.editable) {
    return false;
  }
  return !isEqual(a.persisted, a.editable);
};

const valueIsUpdatedChecker = (a?: number, b?: number) =>
  !!(a !== b && (a || a === 0) && !isNaN(a));
